import { Miscellaneous } from "src/types/graphql.d";
import { interpolate, hasPlaceholder } from "src/utils/templateString";
import { FREE_SHIPPING_THRESHOLD_PLACEHOLDER } from "src/constants/freeShipping";

const DROPPED = Symbol("dropped");

type Resolved<T> = T | typeof DROPPED;

const resolveText = (
  text: string,
  threshold: string | undefined
): Resolved<string> => {
  if (!hasPlaceholder(text, FREE_SHIPPING_THRESHOLD_PLACEHOLDER)) return text;
  if (!threshold) return DROPPED;

  return interpolate(text, {
    [FREE_SHIPPING_THRESHOLD_PLACEHOLDER]: threshold,
  });
};

const resolveEntry = (
  entry: unknown,
  threshold: string | undefined
): Resolved<unknown> => {
  if (typeof entry === "string") return resolveText(entry, threshold);

  if (entry && typeof entry === "object" && !Array.isArray(entry)) {
    const resolved: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(entry)) {
      const next = resolveEntry(value, threshold);
      if (next === DROPPED) return DROPPED;
      resolved[key] = next;
    }

    return resolved;
  }

  return resolveNode(entry, threshold);
};

const resolveNode = (node: unknown, threshold: string | undefined): unknown => {
  if (typeof node === "string") {
    const resolved = resolveText(node, threshold);

    return resolved === DROPPED ? undefined : resolved;
  }

  if (Array.isArray(node)) {
    return node
      .map((item) => resolveEntry(item, threshold))
      .filter((item) => item !== DROPPED);
  }

  if (node && typeof node === "object") {
    return Object.fromEntries(
      Object.entries(node).map(([key, value]) => [
        key,
        resolveNode(value, threshold),
      ])
    );
  }

  return node;
};

export const resolveFreeShippingThreshold = (
  miscellaneous: Miscellaneous,
  threshold: string | undefined
): Miscellaneous => resolveNode(miscellaneous, threshold) as Miscellaneous;
